<template>
  <div class="prescription-scan-view">
    <div class="prescription-scan-view__header">
      <h5 class="prescription-scan-view__title">
        <i class="fas fa-check" /> Escanear Receta
      </h5>
      <el-switch
        v-model="inputCamera"
        style="display: block"
        active-color="#13ce66"
        inactive-color="#ff4949"
        :inactive-text="inputSwitchText" />
    </div>
    <div v-if="error !== null" class="alert alert-danger text-center">
      {{ error }}
    </div>
    <form @submit.prevent="handleFormSubmit">
      <form-group for-id="prescription_id" label="Código de Receta">
        <div class="input-group">
          <input
            id="prescription_id"
            ref="codeInput"
            v-model="foundCode"
            v-mask="'XXXXX-XXXXX-XXXXX-XXXXX'"
            type="text"
            class="form-control">
          <div class="input-group-append">
            <button :disabled="!isValidCode" class="btn btn-primary" type="submit">
              Dispensar
            </button>
          </div>
        </div>
        <small v-if="testingCode" class="form-text text-info">Código para pruebas: <a href="#" @click.prevent="foundCode = testingCode">{{ testingCode }}</a></small>
      </form-group>
    </form>
    <div v-if="inputCamera" class="prescription-scan-view__capture_device form-group mb-2">
      <label for="device-id">Dispositivo de captura</label>
      <select
        id="device-id"
        v-model="selectedDeviceId"
        name="device-id"
        class="form-control">
        <option
          v-for="device in availableDevices"
          :key="device.deviceId"
          :value="device.deviceId">
          {{ device.label }}
        </option>
      </select>
    </div>
    <div v-show="inputCamera" class="prescription-scan-view__video-container">
      <div id="interactive" class="viewport" />
    </div>
    <prescription-preview-dialog
      :visible="showPrescriptionDialog"
      :prescription-id="foundCode"
      @close-dialog="showPrescriptionDialog = false">
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="handleRestartScanner"><i class="fas fa-redo-alt" /> Volver a Escanear</el-button>
      </span>
    </prescription-preview-dialog>
  </div>
</template>

<script>
import Quagga from 'quagga';
import FormGroup from '../components/FormGroup';
import PrescriptionPreviewDialog from '@/components/prescription/PrescriptionPreviewDialog';

const QUAGGA_CONFIG = {
  inputStream: {
    type: 'LiveStream',
    constraints: {
      width: { ideal: 1280 },
      height: { ideal: 720 },
      facingMode: 'environment',
      aspectRatio: { min: 1, max: 2 }
    }
  },
  decoder: {
    readers: ['code_128_reader']
  }
};

const INPUT_CAMERA_KEY = `${process.env.VUE_APP_LOCAL_STORE_PREFIX}input-camera`;
const MIN_CANDIDATES_FOR_MATCH = 20;

export default {
  name: 'PrescriptionScanView',
  components: { FormGroup, PrescriptionPreviewDialog },
  data() {
    return {
      error: null,
      scannerRunning: false,
      quaggaInitialized: false,
      availableDevices: [],
      selectedDeviceId: null,
      foundCode: '',
      candidatesCodes: {},
      resultCollector: null,
      showPrescriptionDialog: null,
      inputCamera: localStorage.getItem(INPUT_CAMERA_KEY) === '1'
    };
  },
  computed: {
    isValidCode() {
      return this.foundCode !== null && this.foundCode.replace(/-/g, '').length === 20;
    },
    prescriptionFrame() {
      return `${process.env.VUE_APP_API_URL}/medication-request/${this.foundCode.replace(/-/g, '')}/preview`;
    },
    inputSwitchText() {
      return this.inputCamera ? 'Deshabilitar Cámara' : 'Habilitar Cámara';
    },
    testingCode() {
      return process.env.NODE_ENV === 'development' ? process.env.VUE_APP_DEV_PRESCRIPTION_ID : null;
    }
  },
  watch: {
    inputCamera: {
      immediate: true,
      handler: function(newValue) {
        if (newValue) {
          this.initDeviceDetection()
            .then(() => {
              this.startScanner();
            });
        } else {
          this.stopScanner();
        }
        localStorage.setItem(`${process.env.VUE_APP_LOCAL_STORE_PREFIX}input-camera`, newValue ? '1' : '0');
      }
    }
  },
  mounted() {
    this.$refs.codeInput.focus();
  },
  beforeDestroy() {
    this.stopScanner();
  },
  methods: {
    handleFormSubmit() {
      this.$router.push(`/prescription-dispense/${this.foundCode}`);
    },
    handleCodeCheck() {
      if (this.isValidCode) {
        this.showPrescriptionDialog = true;
      }
    },
    initDeviceDetection() {
      return Quagga.CameraAccess.enumerateVideoDevices().then((data) => {
        this.availableDevices = data;
        if (data.length) {
          this.selectedDeviceId = data[0].deviceId;
        }
        return Promise.resolve(this.selectedDeviceId);
      });
    },
    async initQuagga() {
      return new Promise((resolve, reject) => {
        Quagga.init(QUAGGA_CONFIG, (err) => {
          if (err) {
            this.handleError(err);
            return reject(err);
          }
          this.quaggaInitialized = true;
          return resolve();
        });
      });
    },
    handleError(error) {
      if (error.name === 'NotAllowedError') {
        this.error = 'Debe permitir acceder a la cámara del dispositivo para poder continuar.';
      } else {
        this.error = 'Ha ocurrido un error al intentar acceder a la cámara del dispositivo.';
      }
    },
    async startScanner() {
      if (!this.quaggaInitialized) {
        await this.initQuagga();
      }
      Quagga.start();
      Quagga.onProcessed(this.handleFrameProcessed);
      this.scannerRunning = true;
      this.foundCode = null;
      this.candidatesCodes = {};
    },
    stopScanner() {
      if (this.quaggaInitialized) {
        Quagga.stop();
      }
      this.quaggaInitialized = false;
      this.scannerRunning = false;
    },
    candidateFound(foundCode) {
      if (!this.candidatesCodes.hasOwnProperty(foundCode)) {
        this.candidatesCodes[foundCode] = 0;
      }
      this.candidatesCodes[foundCode]++;
      if (this.candidatesCodes[foundCode] >= MIN_CANDIDATES_FOR_MATCH) {
        this.foundCode = foundCode;
        return true;
      }
    },
    handleFrameProcessed(data) {
      if (data !== undefined && data.hasOwnProperty('codeResult')) {
        if (this.candidateFound(data.codeResult.code)) {
          this.handleCodeCheck();
          this.stopScanner();
        }
      }
    },
    handleClosePrescriptionDialog() {
      this.showPrescriptionDialog = null;
      this.foundCode = null;
      this.$refs.codeInput.focus();
    },
    handleRestartScanner() {
      this.handleClosePrescriptionDialog();
      if (this.inputCamera) {
        this.startScanner();
      }
    }
  }
};
</script>

<style lang="scss">
  .prescription-scan-view {
    color: $--color-white;

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: baseline;

      .el-switch__label {
        color: $--color-white;
      }
    }

    &__title {
      margin: $--spacing-6 0;
    }

    &__capture_device {
      color: $--color-white;
    }

    &__video-container {
      video {
        max-width: 100%;
      }

      canvas {
        display: none;
      }
    }

    &__preview-frame {
      height: 450px;

      iframe {
        width: 100%;
        height: 100%;
      }
    }
  }
</style>
